import classnames from 'classnames';
import {
    Children,
    cloneElement,
    ReactElement,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import * as React from 'react';
import TabsContext from './TabsContext';
import { TabId, TabListProps, TabProps } from './types';
import './styles/Tablist.scss';

function Tablist({
    children,
    className,
    ariaLabel,
}: TabListProps) {
    const tablistRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<Record<TabId, HTMLButtonElement>>({});
    const {
        selectedTabId, setSelectedTabId, value, variant, isGrid, theme, overflow,
    } = useContext(TabsContext);
    const [indicatorWidth, setIndicatorWidth] = useState<number>();
    const [indicatorLeftPosition, setIndicatorLeftPosition] = useState<number>();
    const hasSelection = selectedTabId !== undefined;
    const classes = classnames('tablist', className);
    const modifier = theme === 'transparent' ? 'scroll' : overflow;
    const tabsContainerClasses = classnames('tablist__tabs-container',
        `tablist__tabs-container--${modifier}`,
        `tablist__tabs-container--${theme}`);

    const handleTabSelection = (id: TabId): void => {
        setSelectedTabId(id);
    };

    useEffect(() => {
        if (!hasSelection && value === undefined) {
            const tabElements = Object.values(tabRefs.current).filter(el => el);
            setSelectedTabId(tabElements[0].id);
        }
    }, [hasSelection, setSelectedTabId, value]);

    useEffect(() => {
        if (!hasSelection) return;

        const tabElements = Object.values(tabRefs.current);
        const newIndex = tabElements
            .findIndex(tabElement => selectedTabId === tabElement.id);

        setIndicatorLeftPosition(
            tabElements
                .slice(0, newIndex > 0 ? newIndex : 0)
                .reduce((result, tabElement) => (result + tabElement.offsetWidth), 0),
        );

        setIndicatorWidth(
            tabElements[newIndex]?.offsetWidth,
        );
    }, [hasSelection, selectedTabId]);

    useEffect(() => {
        const { current: tablistElement } = tablistRef;
        const tabElements = Object.values(tabRefs.current);

        const handleKeydown = (e: KeyboardEvent) => {
            if (e.code !== 'ArrowLeft' && e.code !== 'ArrowRight') return;

            const numberOfTabs = tabElements.length;
            const activeIndex = tabElements.findIndex(child => document.activeElement === child);
            const previousIndex = activeIndex - 1;
            const nextIndex = activeIndex + 1;

            const newIndexByEventCode = {
                ArrowLeft: previousIndex >= 0 ? previousIndex : numberOfTabs - 1,
                ArrowRight: nextIndex < numberOfTabs ? nextIndex : 0,
            };

            const newIndex = newIndexByEventCode[e.code];

            tabElements[newIndex].focus();
        };

        tablistElement?.addEventListener('keydown', handleKeydown);

        return () => {
            tablistElement?.removeEventListener('keydown', handleKeydown);
        };
    }, []);

    return (
        <div
            ref={tablistRef}
            aria-label={ariaLabel}
            className={classes}
        >
            <div
                className={tabsContainerClasses}
                role="tablist"
            >
                {Children.map(children, (child: ReactElement) => {
                    if (!child) { return child; }
                    const { id, iconName, className: childClassName } = child.props;
                    const isSelected = selectedTabId === id;
                    const childProps: Partial<TabProps> & { ref: React.Ref<HTMLButtonElement> } = {
                        ref: (element) => { tabRefs.current[id] = element!; },
                        className: classnames('tablist__tab', childClassName),
                        isSelected,
                        onSelect: handleTabSelection,
                        isGrid,
                        iconName,
                        variant,
                        theme,
                        overflow,
                    };

                    return cloneElement(child, childProps);
                })}
                {!isGrid && variant !== 'hugged' && theme !== 'primary' && (
                    <div
                        className="tablist__tabs-container__selection-indicator"
                        style={{
                            ...(indicatorWidth !== undefined && indicatorWidth >= 0 && {
                                width: `${indicatorWidth}px`,
                                left: `${indicatorLeftPosition}px`,
                            }),
                        }}
                    />
                )}
            </div>
        </div>
    );
}

export default Tablist;
